import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from 'radix-ui';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  EyeOff,
  FileText,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import { PropertySettings } from './PropertySettings';
import {
  addProperty,
  asList,
  changePropertyType,
  deleteProperty,
  isEmptyValue,
  linkTarget,
  PROPERTY_TYPES,
  cellValue,
  rawValue,
  renameProperty,
  setColumnWidth,
  splitDateTime,
  toggleColumn,
  TITLE_ID,
  updateProperty,
  updateView,
  viewColumns,
  type DatabaseSchema,
  type PropertyDef,
  type PropertyType,
  type ViewDef,
} from '@nexus/core';
import { setRowValue, type DatabaseRow } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { cn } from '../../lib/cn';
import { openRow } from '../../lib/peek';
import { formatDue } from '../tasks/format';
import { useSynced } from './useSynced';
import { viewIn, type Save } from './save';
import { ComputedText, OptionChip, PropertyValue, type RelatedDatabases } from './values';

const defaultWidth = (id: string) => (id === TITLE_ID ? 260 : 180);

export interface ViewProps {
  /** The database page. */
  databaseId: string;
  schema: DatabaseSchema;
  /** Databases linked through relations/backlinks. */
  related: RelatedDatabases;
  view: ViewDef;
  rows: DatabaseRow[];
  save: Save;
  add: (values?: Record<string, unknown>) => Promise<void>;
  justAdded: string | null;
  /** A read-only workspace membership: no editing, adding or reordering rows. */
  readOnly?: boolean;
}

const menu =
  'z-[70] flex min-w-48 flex-col rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
const menuItem =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-left outline-none hover:bg-bg-hover data-[highlighted]:bg-bg-hover';

/** Read-only rendering of a value (cards, lists). */
export function ValueText({ def, value }: { def: PropertyDef; value: unknown }) {
  const { t, i18n } = useTranslation();
  if (isEmptyValue(value)) return null;
  switch (def.type) {
    case 'select':
    case 'status':
      return <OptionChip def={def} value={String(value)} />;
    case 'multi_select':
      return (
        <>
          {asList(value).map((v) => (
            <OptionChip key={v} def={def} value={v} />
          ))}
        </>
      );
    case 'checkbox':
      return value === true || value === 'true' ? (
        <span aria-label={def.name}>✓ {def.name}</span>
      ) : null;
    case 'date': {
      const dt = splitDateTime(String(value));
      return dt ? <span>{formatDue(dt.date, i18n.language, t, undefined, dt.time)}</span> : null;
    }
    case 'page':
    case 'relation':
    case 'backlink':
      return <span>{asList(value).map(linkTarget).join(', ')}</span>;
    case 'rollup':
    case 'formula':
      return <ComputedText def={def} value={value} />;
    case 'button':
      return null;
    case 'person':
      return <span>{asList(value).join(', ')}</span>;
    default:
      return <span className="truncate">{String(value)}</span>;
  }
}

/** Row title: editable in place, with a button to open the row as a page. */
function TitleCell({
  row,
  autoFocus,
  readOnly,
}: {
  row: DatabaseRow;
  autoFocus: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const store = useStore();
  // Stays mounted while saving: a remount would steal focus (autoFocus) from
  // whatever the user clicked next, e.g. an open option list.
  const [title, setTitle, focus] = useSynced(row.title);
  const input = useRef<HTMLInputElement>(null);
  // A new row may already be on screen before it is known to be the new one.
  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);
  return (
    <div className="group/title flex min-w-0 items-center gap-1">
      {readOnly ? (
        <span className="min-w-0 flex-1 truncate px-1.5 py-0.5 font-medium">
          {row.title || t('page.untitled')}
        </span>
      ) : (
        <input
          ref={input}
          aria-label={t('db.title')}
          value={title}
          placeholder={t('page.untitled')}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={focus.onFocus}
          onBlur={() => {
            focus.onBlur();
            if (title !== row.title) void store.updatePage(row.id, { title });
          }}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          className="min-w-0 flex-1 rounded bg-transparent px-1.5 py-0.5 font-medium outline-none hover:bg-bg-hover focus:bg-bg-hover"
        />
      )}
      <button
        type="button"
        aria-label={t('db.openRow', { title: row.title || t('page.untitled') })}
        onClick={(e) => openRow(row.id, e)}
        className="opacity-0 flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-xs text-fg-muted group-hover/title:opacity-100 hover:bg-bg-hover focus:opacity-100"
      >
        <FileText className="size-3" />
        {t('db.open')}
      </button>
    </div>
  );
}

/** Types with settings of their own (which database, which expression, …). */
const CONFIGURABLE: PropertyType[] = ['relation', 'backlink', 'rollup', 'formula', 'button'];

function HeaderMenu({
  databaseId,
  schema,
  view,
  def,
  rows,
  save,
}: {
  databaseId: string;
  schema: DatabaseSchema;
  view: ViewDef;
  def: PropertyDef;
  rows: DatabaseRow[];
  save: Save;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(def.name);
  // Open settings for this property (with the type just chosen, before it is saved).
  const [settings, setSettings] = useState<PropertyDef | null>(null);
  const sortBy = (dir: 'asc' | 'desc') =>
    save((s) => updateView(s, { ...viewIn(s, view), sorts: [{ propId: def.id, dir }] }));
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('db.propertyMenu', { name: def.name })}
          className="flex w-full items-center gap-1 truncate rounded px-1.5 py-1 text-left text-xs font-medium text-fg-muted hover:bg-bg-hover"
        >
          <span className="truncate">{def.name}</span>
          <ChevronDown className="size-3 shrink-0 opacity-60" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={4} className={menu}>
          <input
            aria-label={t('db.propertyName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation(); // typing must not trigger menu shortcuts
              if (e.key === 'Enter') save((s) => renameProperty(s, def.id, name));
            }}
            onBlur={() => save((s) => renameProperty(s, def.id, name))}
            className="mb-1 rounded-md border border-border bg-bg px-2 py-1 text-sm outline-none focus:border-accent"
          />
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className={menuItem}>
              {t('db.type')}: {t(`db.types.${def.type}`)}
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className={menu}>
                {PROPERTY_TYPES.map((type) => (
                  <DropdownMenu.Item
                    key={type}
                    className={cn(menuItem, type === def.type && 'font-semibold')}
                    onSelect={() => {
                      save((s) =>
                        changePropertyType(
                          s,
                          def.id,
                          type,
                          rows.map((r) => rawValue(r.props, def)),
                        ),
                      );
                      if (CONFIGURABLE.includes(type) && type !== def.type)
                        setSettings({ ...def, type });
                    }}
                  >
                    {t(`db.types.${type}`)}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          {CONFIGURABLE.includes(def.type) && (
            <DropdownMenu.Item className={menuItem} onSelect={() => setSettings(def)}>
              <Settings2 className="size-3.5" />
              {t('db.settings')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item className={menuItem} onSelect={() => sortBy('asc')}>
            <ArrowUp className="size-3.5" />
            {t('db.sortAsc')}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={menuItem} onSelect={() => sortBy('desc')}>
            <ArrowDown className="size-3.5" />
            {t('db.sortDesc')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={menuItem}
            onSelect={() => save((s) => toggleColumn(s, viewIn(s, view), def.id, false))}
          >
            <EyeOff className="size-3.5" />
            {t('db.hide')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={cn(menuItem, 'text-danger')}
            onSelect={() => save((s) => deleteProperty(s, def.id))}
          >
            <Trash2 className="size-3.5" />
            {t('db.deleteProperty')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
      {settings && (
        <PropertySettings
          databaseId={databaseId}
          schema={schema}
          def={settings}
          save={save}
          open
          onOpenChange={(open) => !open && setSettings(null)}
        />
      )}
    </DropdownMenu.Root>
  );
}

function useCellChange(save: Save) {
  const store = useStore();
  return {
    setValue: (row: DatabaseRow, def: PropertyDef, value: unknown) =>
      void setRowValue(store, row.id, def, value),
    setDef: (def: PropertyDef) => save((s) => updateProperty(s, def)),
  };
}

export function TableView({
  databaseId,
  schema,
  view,
  rows,
  save,
  add,
  justAdded,
  related,
  readOnly,
}: ViewProps) {
  const { t } = useTranslation();
  const store = useStore();
  const columns = viewColumns(schema, view);
  const { setValue, setDef } = useCellChange(save);
  // While dragging a column edge the width lives here; it is saved on release.
  const [live, setLive] = useState<{ id: string; width: number } | null>(null);
  const width = (id: string) =>
    live?.id === id ? live.width : (view.widths?.[id] ?? defaultWidth(id));
  const resizer = (id: string, name: string) =>
    readOnly ? null : (
      <span
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={width(id)}
        aria-valuemin={80}
        aria-valuemax={800}
        aria-label={t('db.resize', { name })}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const step = e.key === 'ArrowLeft' ? -20 : 20;
          save((s) => {
            const v = viewIn(s, view);
            return setColumnWidth(s, v, id, (v.widths?.[id] ?? defaultWidth(id)) + step);
          });
        }}
        onPointerDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const start = width(id);
          let current = start;
          const move = (ev: PointerEvent) => {
            current = Math.max(80, start + ev.clientX - startX);
            setLive({ id, width: current });
          };
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            setLive(null);
            if (current !== start) save((s) => setColumnWidth(s, viewIn(s, view), id, current));
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        }}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-accent/60 focus:bg-accent/60 focus:outline-none"
      />
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-max min-w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col style={{ width: width(TITLE_ID) }} />
          {columns.map((c) => (
            <col key={c.id} style={{ width: width(c.id) }} />
          ))}
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr className="border-b border-border">
            <th
              scope="col"
              className="relative px-1.5 py-1 text-left text-xs font-medium text-fg-muted"
            >
              {t('db.title')}
              {resizer(TITLE_ID, t('db.title'))}
            </th>
            {columns.map((def) => (
              <th
                key={def.id}
                scope="col"
                className="relative border-l border-border p-0 font-normal"
              >
                {readOnly ? (
                  <span className="flex w-full truncate px-1.5 py-1 text-left text-xs font-medium text-fg-muted">
                    {def.name}
                  </span>
                ) : (
                  <HeaderMenu
                    databaseId={databaseId}
                    schema={schema}
                    view={view}
                    def={def}
                    rows={rows}
                    save={save}
                  />
                )}
                {resizer(def.id, def.name)}
              </th>
            ))}
            {!readOnly && (
              <th scope="col" className="border-l border-border p-0">
                <button
                  type="button"
                  aria-label={t('db.addProperty')}
                  onClick={() => save((s) => addProperty(s, t('db.newProperty')).schema)}
                  className="flex w-full justify-center rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
                >
                  <Plus className="size-3.5" />
                </button>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="group border-b border-border"
              data-testid="db-row"
              data-title={row.title}
            >
              <td className="px-0.5 py-0.5">
                <TitleCell row={row} autoFocus={row.id === justAdded} readOnly={readOnly} />
              </td>
              {columns.map((def) => (
                <td key={def.id} className="border-l border-border px-0.5 py-0.5 align-middle">
                  <PropertyValue
                    def={def}
                    value={cellValue(def, row)}
                    onChange={(v) => setValue(row, def, v)}
                    onDefChange={setDef}
                    rowId={row.id}
                    related={related}
                    readOnly={readOnly}
                  />
                </td>
              ))}
              {!readOnly && (
                <td className="border-l border-border text-center">
                  <button
                    type="button"
                    aria-label={t('db.deleteRow', { title: row.title || t('page.untitled') })}
                    onClick={() => void store.trashPage(row.id)}
                    className="opacity-0 rounded p-0.5 text-fg-muted group-hover:opacity-100 hover:bg-bg-hover hover:text-danger focus:opacity-100"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && (
        <button
          type="button"
          onClick={() => void add()}
          className="mt-1 flex items-center gap-1 rounded px-2 py-1 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <Plus className="size-3.5" />
          {t('db.newRow')}
        </button>
      )}
    </div>
  );
}

export function Card({
  row,
  columns,
  hide,
  max = 4,
  tall = false,
}: {
  row: DatabaseRow;
  columns: PropertyDef[];
  hide?: string;
  max?: number;
  tall?: boolean;
}) {
  const { t } = useTranslation();
  const shown = columns.filter((c) => c.id !== hide && !isEmptyValue(cellValue(c, row)));
  // A plain block, clicked or dragged anywhere (browsers do not start a drag on a
  // button); the small button opens the row from the keyboard.
  const title = row.title || t('page.untitled');
  return (
    <div
      onClick={(e) => openRow(row.id, e)}
      className={cn(
        'group/card relative flex w-full cursor-pointer flex-col gap-1 rounded-lg border border-border bg-bg px-2 py-1.5 text-sm hover:bg-bg-hover',
        tall && 'min-h-24',
      )}
    >
      <span className="pr-6 font-medium">{title}</span>
      <button
        type="button"
        aria-label={t('db.openRow', { title })}
        onClick={(e) => {
          e.stopPropagation();
          openRow(row.id, e);
        }}
        className="opacity-0 absolute top-1 right-1 rounded p-0.5 text-fg-muted group-hover/card:opacity-100 hover:bg-bg-active focus:opacity-100"
      >
        <FileText className="size-3.5" />
      </button>
      {shown.length > 0 && (
        <span className="flex flex-wrap items-center gap-1 text-xs text-fg-muted">
          {shown.slice(0, max).map((c) => (
            <ValueText key={c.id} def={c} value={cellValue(c, row)} />
          ))}
        </span>
      )}
    </div>
  );
}

export function BoardView({ schema, view, rows, add, readOnly }: ViewProps) {
  const { t } = useTranslation();
  const store = useStore();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const group = schema.properties.find((p) => p.id === view.groupBy);
  const columns = viewColumns(schema, view);
  if (!group)
    return <p className="py-6 text-center text-sm text-fg-muted">{t('db.chooseGroup')}</p>;
  const lanes = [...(group.options ?? []).map((o) => o.name), ''];
  const laneOf = (row: DatabaseRow) => {
    const v = String(rawValue(row.props, group) ?? '');
    return lanes.find((l) => l.toLowerCase() === v.toLowerCase()) ?? '';
  };
  const drop = (lane: string) => {
    if (readOnly) return;
    const row = rows.find((r) => r.id === dragging);
    setDragging(null);
    setOver(null);
    if (row && laneOf(row) !== lane) void setRowValue(store, row.id, group, lane || null);
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {lanes.map((lane) => {
        const list = rows.filter((r) => laneOf(r) === lane);
        if (!lane && list.length === 0) return null;
        return (
          <section
            key={lane || '__none'}
            aria-label={lane || t('db.noValue', { name: group.name })}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(lane);
            }}
            onDragLeave={() => setOver((o) => (o === lane ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              drop(lane);
            }}
            className={cn(
              'flex w-64 shrink-0 flex-col gap-1.5 rounded-lg bg-bg-subtle p-2',
              over === lane && 'ring-2 ring-accent',
            )}
          >
            <h3 className="flex items-center gap-2 px-1 text-sm font-semibold">
              {lane ? (
                <OptionChip def={group} value={lane} />
              ) : (
                t('db.noValue', { name: group.name })
              )}
              <span className="text-xs font-normal text-fg-muted">{list.length}</span>
            </h3>
            {list.map((row) => (
              <div
                key={row.id}
                draggable={!readOnly}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', row.id);
                  setDragging(row.id);
                }}
                onDragEnd={() => setDragging(null)}
                className={cn(!readOnly && 'cursor-grab', dragging === row.id && 'opacity-50')}
                data-testid="db-card"
              >
                <Card row={row} columns={columns} hide={group.id} />
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                onClick={() => void add(lane ? { [group.id]: lane } : {})}
                className="flex items-center gap-1 rounded px-1 py-0.5 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg"
              >
                <Plus className="size-3" />
                {t('db.newRow')}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function ListView({ schema, view, rows }: ViewProps) {
  const { t } = useTranslation();
  const columns = viewColumns(schema, view);
  if (!rows.length)
    return <p className="py-6 text-center text-sm text-fg-muted">{t('db.noRows')}</p>;
  return (
    <ul className="flex flex-col">
      {rows.map((row) => (
        <li key={row.id} data-testid="db-row">
          <button
            type="button"
            onClick={(e) => openRow(row.id, e)}
            className="flex w-full flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-hover"
          >
            <FileText className="size-4 shrink-0 text-fg-muted" />
            <span className="font-medium">{row.title || t('page.untitled')}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
              {columns
                .filter((c) => !isEmptyValue(cellValue(c, row)))
                .map((c) => (
                  <ValueText key={c.id} def={c} value={cellValue(c, row)} />
                ))}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
